<template>
  <div class="fee-rules-page">
    <div v-if="hasData" class="rule-overview">
      <div class="overview-title">集采收费规则</div>
      <div v-if="noticeText" class="overview-notice">{{ noticeText }}</div>
    </div>

    <div v-if="hasData" class="rule-list">
      <section
        v-for="section in sections"
        :key="section.feeType"
        class="rule-section"
      >
        <div class="section-header">
          <div class="section-title">{{ section.feeTypeName }}</div>
        </div>

        <div
          v-for="rule in section.rules"
          :key="rule.ruleId"
          class="rule-card"
        >
          <div class="rule-card-header">
            <div class="rule-name">{{ rule.ruleName }}</div>
          </div>
          <div v-if="rule.formulaText" class="formula-text">
            {{ rule.formulaText }}
          </div>
          <div v-if="rule.detailText" class="rule-detail">
            {{ rule.detailText }}
          </div>
          <div v-if="rule.settlementTip" class="settlement-tip">
            {{ rule.settlementTip }}
          </div>
          <div v-if="rule.tiers && rule.tiers.length" class="tier-list">
            <div
              v-for="(tier, index) in rule.tiers"
              :key="index"
              class="tier-item"
            >
              <span>{{ formatTierRange(tier) }}</span>
              <strong>{{ formatTierFee(tier) }}</strong>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-if="!loading && !hasData" class="empty-wrapper">
      <div class="empty-title">暂无服务费规则</div>
      <div class="empty-desc">当前区域暂未配置可展示的服务费规则</div>
    </div>
  </div>
</template>

<script>
import { navBar } from '@/utils/native_interactions/native_bar'
import { API } from '@/api'
import Url from '@mc/mc-utils/js/url/url'
import { feeRulesMockData } from './mock'

export default {
  beforeRouteEnter(to, from, next) {
    next(vm => {
      navBar.setTitle({
        text: '集采收费规则'
      })
      document.title = '集采收费规则'
      vm.getFeeRules()
    })
  },
  data() {
    return {
      loading: false,
      sections: [],
      noticeText: ''
    }
  },
  computed: {
    hasData() {
      return this.sections.some(
        section => Array.isArray(section.rules) && section.rules.length
      )
    },
    useMock() {
      const url = new Url(window.location.href)
      const query = url.parseQuerystring()
      const hashQuery = url.Hash ? url.Hash.parseQuerystring() : {}
      return query.mock === '1' || hashQuery.mock === '1'
    }
  },
  methods: {
    async getFeeRules() {
      this.loading = true
      this.$Loading()
      try {
        const data = this.useMock
          ? feeRulesMockData
          : await API.shippingFee.getFeeRulesDisplay({})
        this.setPageData(data || {})
      } catch (error) {
        this.sections = []
        this.$catch(error)
      }
      this.loading = false
      this.$Loading.hide()
    },
    setPageData(data) {
      this.noticeText = data.noticeText || ''
      this.sections = this.normalizeSections(data.sections || [])
    },
    normalizeSections(sections) {
      return sections
        .map(section => ({
          ...section,
          rules: Array.isArray(section.rules) ? section.rules : []
        }))
        .filter(section => section.feeTypeName)
    },
    formatTierRange(tier) {
      const min = this.formatAmount(tier.min)
      if (tier.max === null || tier.max === undefined || tier.max === '') {
        return `${min}元以上`
      }
      return `${min}-${this.formatAmount(tier.max)}元`
    },
    formatTierFee(tier) {
      const fee = this.formatAmount(tier.fee)
      return `${fee}${tier.unit || '元'}`
    },
    formatAmount(value) {
      const numberValue = Number(value)
      if (!Number.isFinite(numberValue)) {
        return value
      }
      return String(Number(numberValue.toFixed(2)))
    }
  }
}
</script>

<style scoped lang="scss">
.fee-rules-page {
  min-height: 100vh;
  padding: 0.24rem 0.24rem 0.4rem;

  background-color: #f5f6f7;
  color: #262626;
}

.rule-overview {
  padding: 0.32rem 0.28rem;

  background: linear-gradient(135deg, #e9fbf2 0%, #f7fcfa 55%, #ffffff 100%);
  border: 1px solid #dff3e9;
  border-radius: 0.12rem;
}

.overview-title {
  font-size: 0.4rem;
  font-weight: 600;
  line-height: 0.56rem;
}

.overview-notice {
  margin-top: 0.16rem;

  font-size: 0.26rem;
  line-height: 0.4rem;
  color: #8a8a8a;
}

.rule-list {
  margin-top: 0.24rem;
}

.rule-section {
  margin-bottom: 0.24rem;
  padding: 0.28rem;

  background-color: #ffffff;
  border-radius: 0.12rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  font-size: 0.34rem;
  font-weight: 600;
  line-height: 0.48rem;
}

.rule-card {
  margin-top: 0.2rem;
  padding: 0.24rem;

  background-color: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 0.08rem;
}

.rule-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.rule-name {
  flex: 1;
  min-width: 0;

  font-size: 0.3rem;
  font-weight: 600;
  line-height: 0.44rem;
}

.formula-text,
.rule-detail {
  margin-top: 0.12rem;

  font-size: 0.26rem;
  line-height: 0.4rem;
  color: #595959;
}

.formula-text {
  color: #262626;
  font-weight: 600;
}

.settlement-tip {
  margin-top: 0.12rem;
  padding: 0.16rem;

  font-size: 0.26rem;
  line-height: 0.4rem;
  color: #7a5529;

  background-color: #fff8ed;
  border-radius: 0.08rem;
}

.tier-list {
  margin-top: 0.16rem;

  border-top: 1px solid #eeeeee;
}

.tier-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 0.72rem;

  font-size: 0.26rem;
  color: #595959;

  border-bottom: 1px solid #eeeeee;

  &:last-child {
    border-bottom: 0;
  }

  strong {
    flex: none;
    margin-left: 0.2rem;

    color: #262626;
    font-weight: 600;
  }
}

.empty-wrapper {
  padding-top: 2.4rem;

  text-align: center;
}

.empty-title {
  font-size: 0.32rem;
  font-weight: 600;
  line-height: 0.48rem;
}

.empty-desc {
  margin-top: 0.12rem;

  font-size: 0.26rem;
  line-height: 0.4rem;
  color: #8a8a8a;
}
</style>
